import { ROMA_MENU_ITEMS } from "../../convex/romaMenuSeedData";
import type { RestaurantSiteConfig } from "./restaurant-platform";
import { tuscanCoveCateringMenuSections } from "./tuscan-cove-content";

function parsePrice(price: string) {
  const match = price.match(/\d+(?:\.\d+)?/);
  return match ? Number.parseFloat(match[0]) : 0;
}

function isRomaSiteConfig(siteConfig: RestaurantSiteConfig) {
  return siteConfig.name === "Roma Kitchen";
}

function isTuscanSiteConfig(siteConfig: RestaurantSiteConfig) {
  return siteConfig.name === "Tuscan Cove Bar & Patio";
}

export type LeadRouteCateringMenuItem = {
  name: string;
  description: string;
  price: string;
  sectionTitle: string;
  image?: string;
};

export type LeadRouteFaqItem = {
  question: string;
  answer: string;
};

const tuscanCateringFaqItems: LeadRouteFaqItem[] = [
  {
    question: "Order online or send an inquiry?",
    answer:
      "Know your trays? Order online for pickup. Need planning, a custom menu, or a larger group meal? Use the catering inquiry form on this page.",
  },
  {
    question: "What should I include for a quote?",
    answer:
      "Include your event date, pickup or service timing, estimated headcount, event type, location, menu direction, and any dietary notes so the team can respond quickly.",
  },
  {
    question: "Are catering trays available for pickup?",
    answer:
      "Yes. The catering menu includes group-friendly pickup options such as pizza bundles, wings, tenders, sliders, sandwiches, fajitas, fries, and salads.",
  },
];

const romaCateringFaqItems: LeadRouteFaqItem[] = [
  {
    question: "Order online or send a Roma Kitchen catering inquiry?",
    answer:
      "Use the catering inquiry form when you need trays, group meal planning, pickup timing, delivery notes, or help choosing the right Italian menu direction.",
  },
  {
    question: "What should I include for a Roma Kitchen catering quote?",
    answer:
      "Include your event date, pickup or service timing, estimated headcount, event type, location, menu direction, and any dietary notes so the team can respond quickly.",
  },
  {
    question: "Are Roma Kitchen catering trays available for pickup?",
    answer:
      "Yes. Roma Kitchen catering includes group-friendly Italian trays such as antipasti, salads, pasta trays, Chicken Parmigiana, Eggplant Parmigiano, sides, and desserts.",
  },
];

export function buildCateringFaqItemsForSite(
  siteConfig: RestaurantSiteConfig,
): LeadRouteFaqItem[] {
  if (isRomaSiteConfig(siteConfig)) {
    return romaCateringFaqItems;
  }

  // Tuscan-specific catering FAQ (pizza bundles, wings, sliders, etc.) must not
  // bleed onto other tenants. Only Tuscan gets the Tuscan set; brands without an
  // authored, brand-correct set get none.
  return isTuscanSiteConfig(siteConfig) ? tuscanCateringFaqItems : [];
}

export function buildCateringMenuItemsForSite(
  siteConfig: RestaurantSiteConfig,
): LeadRouteCateringMenuItem[] {
  if (isRomaSiteConfig(siteConfig)) {
    return ROMA_MENU_ITEMS.filter((item) => item.section === "catering")
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({
        name: item.name,
        description: item.description,
        price: item.fullPriceText
          ? `${item.priceText} / ${item.fullPriceText}`
          : item.priceText,
        sectionTitle: item.originalSection,
        image: item.photoUrl,
      }));
  }

  // Tuscan-only catering menu (Stone Fired Pizza, Double Smash Burger, Carne
  // Asada, etc.) must not bleed onto other tenants. Only Tuscan emits it; brands
  // without authored catering data get an empty menu.
  if (!isTuscanSiteConfig(siteConfig)) {
    return [];
  }

  return tuscanCoveCateringMenuSections.flatMap((section) =>
    section.items.map((item) => ({
      name: item.name,
      description: item.description ?? "",
      price: item.price,
      sectionTitle: section.title,
      image: item.image,
    })),
  );
}

function buildTuscanCateringMenuSections(siteConfig: RestaurantSiteConfig) {
  const sections = new Map<string, LeadRouteCateringMenuItem[]>();

  for (const item of buildCateringMenuItemsForSite(siteConfig)) {
    const sectionItems = sections.get(item.sectionTitle) ?? [];
    sectionItems.push(item);
    sections.set(item.sectionTitle, sectionItems);
  }

  return [...sections.entries()].map(([sectionName, items]) => ({
    "@type": "MenuSection",
    name: sectionName,
    hasMenuItem: items.map((item) => ({
      "@type": "MenuItem",
      name: item.name,
      description: item.description || `${siteConfig.name} catering menu item.`,
      offers: {
        "@type": "Offer",
        price: parsePrice(item.price),
        priceCurrency: "USD",
      },
    })),
  }));
}

/**
 * Pulls the serving count for one tray size out of a catering item's
 * description ("Half serves 6-8; full serves 12-14.") and returns it as a
 * QuantitativeValue, or undefined when that item states no count.
 *
 * The counts were already published in prose, so every consumer that could not
 * read a sentence — which includes anything consuming the JSON-LD — saw a tray
 * with a price and no yield. "How many people does a half tray feed" is the
 * single most common catering question and it was answerable only by a human
 * reading the menu.
 *
 * Modeled as `Offer.eligibleQuantity` because each offer IS one sellable tray
 * size; the quantity describes what that tray yields, in servings.
 *
 * Returns undefined rather than guessing. Three Roma trays (Shrimp Scampi,
 * Fried Calamari, Crostini with Prosciutto) say only "Half tray / full tray"
 * because their yield has never been confirmed; they emit no count until it is.
 */
function parseServingCount(
  description: string,
  size: "half" | "full",
): Record<string, unknown> | undefined {
  const match = new RegExp(
    `${size}\\s+serves\\s+(\\d+)(?:\\s*[-–]\\s*(\\d+))?`,
    "i",
  ).exec(description);

  if (!match) {
    return undefined;
  }

  const min = Number.parseInt(match[1], 10);
  const max = match[2] ? Number.parseInt(match[2], 10) : undefined;

  return {
    "@type": "QuantitativeValue",
    ...(max === undefined
      ? { value: min }
      : { minValue: min, maxValue: max }),
    unitText: "servings",
  };
}

function buildRomaCateringMenuSections() {
  const sections = new Map<
    string,
    (typeof ROMA_MENU_ITEMS)[number][]
  >();

  for (const item of ROMA_MENU_ITEMS) {
    if (item.section !== "catering") continue;

    const sectionItems = sections.get(item.originalSection) ?? [];
    sectionItems.push(item);
    sections.set(item.originalSection, sectionItems);
  }

  return [...sections.entries()].map(([sectionName, items]) => ({
    "@type": "MenuSection",
    name: sectionName,
    hasMenuItem: items
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => {
        const halfServes = parseServingCount(item.description, "half");
        const fullServes = parseServingCount(item.description, "full");

        return {
          "@type": "MenuItem",
          name: item.name,
          description: item.description,
          offers: [
            {
              "@type": "Offer",
              name: "Half tray",
              price: item.price,
              priceCurrency: "USD",
              ...(halfServes ? { eligibleQuantity: halfServes } : {}),
            },
            ...(item.fullPrice
              ? [
                  {
                    "@type": "Offer",
                    name: "Full tray",
                    price: item.fullPrice,
                    priceCurrency: "USD",
                    ...(fullServes ? { eligibleQuantity: fullServes } : {}),
                  },
                ]
              : []),
          ],
        };
      }),
  }));
}

export function buildCateringMenuJsonLd(siteConfig: RestaurantSiteConfig) {
  return {
    "@context": "https://schema.org",
    "@type": "Menu",
    "@id": `${siteConfig.baseUrl}/catering#menu`,
    name: `${siteConfig.name} Catering Menu`,
    // "Las Vegas" is Tuscan-only; other tenants
    // derive the location from their own address so they never claim Las Vegas.
    description: isTuscanSiteConfig(siteConfig)
      ? `Catering trays, group meals, and party options from ${siteConfig.name} in Las Vegas.`
      : `Catering trays, group meals, and party options from ${siteConfig.name} in ${siteConfig.address.city}, ${siteConfig.address.state}.`,
    url: `${siteConfig.baseUrl}/catering`,
    mainEntityOfPage: `${siteConfig.baseUrl}/catering`,
    inLanguage: "en-US",
    hasMenuSection: isRomaSiteConfig(siteConfig)
      ? buildRomaCateringMenuSections()
      : buildTuscanCateringMenuSections(siteConfig),
  };
}

// Roma caters from two kitchens — Southern Highlands and Henderson — so its
// service area spans both valleys instead of the generic [city, state] pair.
const ROMA_CATERING_AREAS = [
  "Las Vegas",
  "Henderson",
  "Southern Highlands",
  "Enterprise",
  "Green Valley",
  "Seven Hills",
  "Anthem",
];

export function buildCateringServiceJsonLd(siteConfig: RestaurantSiteConfig) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteConfig.baseUrl}/catering#service`,
    name: `${siteConfig.name} Catering`,
    serviceType: "Catering",
    provider: {
      "@id": `${siteConfig.baseUrl}/#restaurant`,
    },
    // Southern Highlands and the surrounding Las Vegas suburbs are Tuscan-only
    // neighborhoods. Other tenants derive their
    // area served from their own address so they never claim Las Vegas.
    areaServed: isTuscanSiteConfig(siteConfig)
      ? [
          "Southern Highlands",
          "Las Vegas",
          "Enterprise",
          "Mountain's Edge",
          "Spring Valley",
        ]
      : isRomaSiteConfig(siteConfig)
        ? ROMA_CATERING_AREAS
        : [siteConfig.address.city, siteConfig.address.state],
    url: `${siteConfig.baseUrl}/catering`,
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "USD",
      url: `${siteConfig.baseUrl}/catering`,
    },
  };
}

export function buildJobsWebPageJsonLd(siteConfig: RestaurantSiteConfig) {
  const url = `${siteConfig.baseUrl}/jobs`;

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: `Restaurant Jobs at ${siteConfig.name}`,
    url,
    description: isTuscanSiteConfig(siteConfig)
      ? `Apply for restaurant jobs at ${siteConfig.name} in Southern Highlands, Las Vegas.`
      : `Apply for restaurant jobs at ${siteConfig.name} in ${siteConfig.address.city}, ${siteConfig.address.state}.`,
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteConfig.baseUrl}/#website`,
      name: siteConfig.name,
      url: siteConfig.baseUrl,
    },
    about: { "@id": `${siteConfig.baseUrl}/#restaurant` },
    mainEntity: { "@id": `${siteConfig.baseUrl}/#restaurant` },
    inLanguage: "en-US",
  };
}

export function buildJobsBreadcrumbJsonLd(siteConfig: RestaurantSiteConfig) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { name: "Home", url: siteConfig.baseUrl },
      { name: "Careers", url: `${siteConfig.baseUrl}/jobs` },
    ].map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
